import { logger } from '@/lib/logger';
import { logAuthSuccess, logAuthFailure } from '@/services/securityAuditService';

const clientEnv: Record<string, string | undefined> = {
  VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
  VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
  VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT,
  VITE_CLERK_PUBLISHABLE_KEY: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
};

const processEnv: Record<string, string | undefined> =
  typeof process !== 'undefined' && process.env
    ? (process.env as Record<string, string | undefined>)
    : {};

function readEnv(key: string): string | undefined {
  if (clientEnv[key] != null) {
    return clientEnv[key];
  }

  const fallbackKey = key.startsWith('VITE_') ? key.slice(5) : key;

  if (processEnv[key] != null) {
    return processEnv[key];
  }

  if (processEnv[fallbackKey] != null) {
    return processEnv[fallbackKey];
  }

  if (typeof window !== 'undefined') {
    const win = window as Record<string, unknown>;
    if (typeof win[key] === 'string') {
      return win[key] as string;
    }

    if (typeof win[fallbackKey] === 'string') {
      return win[fallbackKey] as string;
    }
  }

  return undefined;
}

const SUPABASE_URL: string = readEnv('VITE_SUPABASE_URL') || '';
const SUPABASE_PUBLISHABLE_KEY: string = readEnv('VITE_SUPABASE_ANON_KEY') || '';
const ENVIRONMENT_VALUE: string = (readEnv('VITE_ENVIRONMENT') || '').toLowerCase();
const CLERK_PUBLISHABLE_KEY = readEnv('VITE_CLERK_PUBLISHABLE_KEY');

const CLERK_READY_MAX_ATTEMPTS = 60; // ~6s
const CLERK_READY_DELAY_MS = 100;
const TOKEN_REFRESH_BUFFER_MS = 30_000; // refresh 30s before expiry
// VF-61: if the token Clerk hands back dies sooner than this, force-mint a
// fresh one (Clerk's own getToken cache can return a nearly-expired token).
const TOKEN_MIN_REMAINING_MS = 15_000;

const STORAGE_KEYS = {
  clerkUserId: 'clerk_user_id',
  organizationId: 'current_org_id',
};

type EnvironmentName = 'LOCAL' | 'STAGING' | 'PRODUCTION';

type ClerkSessionContext = {
  clerk: any;
  session: any;
  organizationId: string | null;
  userId: string | null;
};

let cachedToken: string | null = null;
let cachedTokenExpiry = 0;
let pendingTokenRequest: Promise<string | null> | null = null;
let lastLoggedToken: string | null = null; // Track if we've logged this token to prevent flooding

function wait(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function isLocalSupabaseUrl(): boolean {
  return SUPABASE_URL.includes('localhost') || SUPABASE_URL.includes('127.0.0.1');
}

export function getEnvironmentName(): EnvironmentName {
  if (isLocalSupabaseUrl() && (import.meta.env.DEV || ENVIRONMENT_VALUE === 'development')) {
    return 'LOCAL';
  }

  if (ENVIRONMENT_VALUE === 'staging') {
    return 'STAGING';
  }

  return 'PRODUCTION';
}

export function shouldSkipAuth(): boolean {
  return getEnvironmentName() === 'LOCAL';
}

export function isClerkEnabled(): boolean {
  return Boolean(CLERK_PUBLISHABLE_KEY);
}

function safeSetLocalStorage(key: string, value: string | null) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    if (value) {
      window.localStorage.setItem(key, value);
    } else {
      window.localStorage.removeItem(key);
    }
  } catch (error) {
    logger.debug('Unable to persist value to localStorage', { key, error });
  }
}

function safeSetSessionStorage(key: string, value: string | null) {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }

    if (value) {
      window.sessionStorage.setItem(key, value);
    } else {
      window.sessionStorage.removeItem(key);
    }
  } catch (error) {
    logger.debug('Unable to persist value to sessionStorage', { key, error });
  }
}

function safeGetStorage(key: string, type: 'local' | 'session'): string | null {
  try {
    if (typeof window === 'undefined') {
      return null;
    }

    if (type === 'local') {
      return window.localStorage?.getItem(key) ?? null;
    }

    return window.sessionStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function persistAuthContext(context: { userId: string | null; organizationId: string | null }) {
  safeSetLocalStorage(STORAGE_KEYS.clerkUserId, context.userId);
  safeSetSessionStorage(STORAGE_KEYS.organizationId, context.organizationId);
}

function parseJwt(token: string | null): { exp?: number } | null {
  if (!token) {
    return null;
  }

  try {
    const [, payloadSegment] = token.split('.');
    if (!payloadSegment) {
      return null;
    }

    const payloadJson = atob(payloadSegment.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payloadJson);
  } catch (error) {
    logger.debug('Unable to parse JWT payload', error);
    return null;
  }
}

async function waitForClerk(): Promise<any | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  for (let attempt = 0; attempt < CLERK_READY_MAX_ATTEMPTS; attempt++) {
    const clerk = (window as any).Clerk;
    if (clerk?.loaded) {
      return clerk;
    }

    await wait(CLERK_READY_DELAY_MS);
  }

  logger.warn('Clerk did not finish loading within expected time');
  return (window as any).Clerk || null;
}

export function getCachedClerkUserId(): string | null {
  return safeGetStorage(STORAGE_KEYS.clerkUserId, 'local');
}

export function getCachedOrganizationId(): string | null {
  return safeGetStorage(STORAGE_KEYS.organizationId, 'session');
}

export function clearCachedAuthContext(): void {
  safeSetLocalStorage(STORAGE_KEYS.clerkUserId, null);
  safeSetSessionStorage(STORAGE_KEYS.organizationId, null);
}

async function resolveOrganizationId(clerk: any, session: any): Promise<string | null> {
  try {
    const orgFromInstance = clerk?.organization?.id;
    if (orgFromInstance) {
      return orgFromInstance;
    }

    const sessionOrg = session?.organization?.id || session?.lastActiveOrganizationId;
    if (sessionOrg) {
      return sessionOrg;
    }

    const userOrgMemberships = clerk?.user?.organizationMemberships;
    if (Array.isArray(userOrgMemberships) && userOrgMemberships.length > 0) {
      return userOrgMemberships[0]?.organization?.id || null;
    }
  } catch (error) {
    logger.debug('Unable to resolve Clerk organization id', error);
  }

  return null;
}

export async function ensureClerkSession(
  options: { requireSignedIn?: boolean } = {},
): Promise<ClerkSessionContext | null> {
  const { requireSignedIn = true } = options;

  if (!isClerkEnabled() || typeof window === 'undefined') {
    return null;
  }

  const clerk = await waitForClerk();
  if (!clerk) {
    logger.warn('Clerk instance unavailable');
    return null;
  }

  const session = clerk.session;
  const user = clerk.user;

  if (requireSignedIn && (!session || !user)) {
    logger.debug('Clerk session not ready for authenticated operations');
    return null;
  }

  const userId = user?.id || null;
  const organizationId = await resolveOrganizationId(clerk, session);

  persistAuthContext({ userId, organizationId });

  return {
    clerk,
    session,
    organizationId,
    userId,
  };
}

async function fetchClerkSupabaseToken(
  template = 'supabase',
  forceRefresh = false,
  allowSkipAuth = false,
): Promise<string | null> {
  // If allowSkipAuth is false, we must get a real JWT even if shouldSkipAuth() is true
  // Only skip if allowSkipAuth is true AND shouldSkipAuth() is true
  if ((shouldSkipAuth() && allowSkipAuth) || !isClerkEnabled()) {
    return null;
  }

  const now = Date.now();
  if (!forceRefresh && cachedToken && cachedTokenExpiry > now) {
    return cachedToken;
  }

  if (pendingTokenRequest) {
    return pendingTokenRequest;
  }

  pendingTokenRequest = (async () => {
    try {
      const context = await ensureClerkSession();
      if (!context?.session) {
        logger.debug('Unable to fetch Clerk JWT: session not ready');
        // Don't log failures for session not ready (normal state during page load)
        return null;
      }

      // VF-61: Clerk's session.getToken() caches minted tokens internally for
      // ~60s. With a 60s template TTL and our 30s refresh buffer, that cache
      // can hand back a token that is about to expire (or already expired),
      // producing a ~1-minute burst of 401s until Clerk's cache rotates.
      // skipCache forces Clerk to mint a genuinely fresh token in those cases.
      let token: string | null = await context.session.getToken({
        template,
        skipCache: forceRefresh,
      });
      if (token && !forceRefresh) {
        const exp = parseJwt(token)?.exp;
        if (exp && exp * 1000 - Date.now() < TOKEN_MIN_REMAINING_MS) {
          logger.debug('Clerk token near expiry — re-minting with skipCache');
          token = await context.session.getToken({ template, skipCache: true });
        }
      }
      if (!token) {
        logger.warn('Clerk returned empty Supabase token');
        await logAuthFailure(context.userId || undefined, 'Empty token returned from Clerk', {
          template,
        });
        return null;
      }

      const payload = parseJwt(token);
      const expiresAt = payload?.exp ? payload.exp * 1000 : now + TOKEN_REFRESH_BUFFER_MS;

      cachedToken = token;
      cachedTokenExpiry = expiresAt - TOKEN_REFRESH_BUFFER_MS;

      // Ensure auth context is persisted whenever we fetch a fresh token
      persistAuthContext({ userId: context.userId, organizationId: context.organizationId });

      // Log successful authentication ONLY if this is a new token (prevents flooding)
      if (token !== lastLoggedToken) {
        lastLoggedToken = token;
        // Fire-and-forget to avoid blocking JWT return
        logAuthSuccess(context.userId || 'unknown', context.organizationId || 'unknown', {
          template,
        }).catch(err => logger.debug('Auth success logging failed', err));
      }

      return token;
    } catch (error) {
      if (
        allowSkipAuth &&
        error instanceof Error &&
        (error.message.includes('No JWT template exists') || error.message.includes('404'))
      ) {
        logger.debug('Clerk Supabase JWT template unavailable; proceeding without token');
        return null;
      }

      logger.error('Failed to retrieve Clerk Supabase token', error);
      // Log auth failure (fire-and-forget to avoid blocking error handling)
      logAuthFailure(undefined, error instanceof Error ? error.message : 'Unknown error', {
        template,
      }).catch(err => logger.debug('Auth failure logging failed', err));
      return null;
    } finally {
      pendingTokenRequest = null;
    }
  })();

  return pendingTokenRequest;
}

export async function getClerkSupabaseJwt(
  options: { forceRefresh?: boolean; template?: string; allowSkipAuth?: boolean } = {},
): Promise<string | null> {
  const { forceRefresh = false, template = 'supabase', allowSkipAuth = false } = options;

  if (forceRefresh) {
    cachedToken = null;
    cachedTokenExpiry = 0;
  }

  const token = await fetchClerkSupabaseToken(template, forceRefresh, allowSkipAuth);
  if (!token && !shouldSkipAuth()) {
    logger.debug('Clerk JWT unavailable - authenticated requests may fail');
  }

  return token;
}

export async function getOrganizationContext(): Promise<{
  userId: string | null;
  organizationId: string | null;
}> {
  const context = await ensureClerkSession({ requireSignedIn: false });
  if (!context) {
    return {
      userId: getCachedClerkUserId(),
      organizationId: getCachedOrganizationId(),
    };
  }

  return {
    userId: context.userId,
    organizationId: context.organizationId,
  };
}

export async function requireAuthenticatedContext(): Promise<ClerkSessionContext | null> {
  return ensureClerkSession({ requireSignedIn: true });
}

export function invalidateCachedClerkToken(): void {
  cachedToken = null;
  cachedTokenExpiry = 0;
}

export async function buildSupabaseAuthHeaders(
  init: HeadersInit = {},
  options: { requireJwt?: boolean; preferJwt?: boolean } = {},
): Promise<Record<string, string>> {
  const { requireJwt = false, preferJwt = false } = options;
  const headers = new Headers(init as HeadersInit);

  const supabaseAnonKey = SUPABASE_PUBLISHABLE_KEY || readEnv('VITE_SUPABASE_ANON_KEY') || '';

  if (supabaseAnonKey) {
    headers.set('apikey', supabaseAnonKey);
  }

  // If requireJwt is true, force real auth even in local development
  const forceRealAuth = requireJwt;
  const skipAuth = shouldSkipAuth() && !forceRealAuth;
  const attachJwt = isClerkEnabled() && (!skipAuth || forceRealAuth);
  const shouldAttemptJwt =
    (!skipAuth || forceRealAuth) && isClerkEnabled() && (preferJwt || requireJwt);

  if (skipAuth && !forceRealAuth) {
    const key = supabaseAnonKey;
    if (key) {
      headers.set('apikey', key);
      if (!headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${key}`);
      }
    }
    return Object.fromEntries(headers.entries());
  }

  // Get Clerk JWT when needed
  if (attachJwt && !headers.has('Authorization')) {
    const token = await getClerkSupabaseJwt({ allowSkipAuth: false });

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    } else if (requireJwt) {
      // Provide more detailed error for debugging
      const clerkEnabled = isClerkEnabled();
      const skipAuth = shouldSkipAuth();
      logger.error('Failed to get Clerk JWT', {
        clerkEnabled,
        skipAuth,
        requireJwt,
        forceRealAuth,
        windowDefined: typeof window !== 'undefined',
      });
      throw new Error(
        'Failed to attach Clerk authentication token. Please ensure you are logged in with Clerk.',
      );
    }
  }

  return Object.fromEntries(headers.entries());
}
